<template>
  <v-alert
    v-bind="$attrs"
    :type="computedType"
    :variant="computedVariant"
    :closable="closable"
    :prominent="prominent"
    :density="density"
    :class="alertClasses"
    class="ds-alert"
    @click:close="handleClose"
  >
    <template #prepend v-if="!hideIcon">
      <ds-icon :ds-size="iconSize" :ds-color="iconColor">
        {{ computedIcon }}
      </ds-icon>
    </template>

    <template #title v-if="title">
      <span class="ds-alert__title">{{ title }}</span>
    </template>

    <div class="ds-alert__content">
      <slot>{{ message }}</slot>
    </div>

    <template #append v-if="$slots.actions">
      <div class="ds-alert__actions">
        <slot name="actions" />
      </div>
    </template>
  </v-alert>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { DsIcon } from '@/shared/components/atoms'

export interface DsAlertProps {
  /**
   * Tipo de alerta
   * @default 'info'
   */
  type?: 'success' | 'info' | 'warning' | 'error'
  /**
   * Variante visual
   * @default 'tonal'
   */
  variant?: 'flat' | 'tonal' | 'outlined' | 'text' | 'elevated'
  /**
   * Título de la alerta
   */
  title?: string
  /**
   * Mensaje de la alerta
   */
  message?: string
  /**
   * Icono personalizado (sobrescribe el icono por defecto del tipo)
   */
  icon?: string
  /**
   * Ocultar icono
   * @default false
   */
  hideIcon?: boolean
  /**
   * Tamaño del icono
   * @default 'md'
   */
  iconSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  /**
   * Si se puede cerrar
   * @default false
   */
  closable?: boolean
  /**
   * Si el alert es prominente (más grande)
   * @default false
   */
  prominent?: boolean
  /**
   * Densidad del alert
   * @default 'default'
   */
  density?: 'default' | 'comfortable' | 'compact'
  /**
   * Color personalizado del icono
   */
  iconColor?: 'primary' | 'accent' | 'success' | 'error' | 'warning' | 'info' | 'gray'
}

const props = withDefaults(defineProps<DsAlertProps>(), {
  type: 'info',
  variant: 'tonal',
  hideIcon: false,
  iconSize: 'md',
  closable: false,
  prominent: false,
  density: 'default'
})

const emit = defineEmits<{
  'close': []
}>()

// Mapeo de tipos a tipos de Vuetify
const computedType = computed(() => {
  return props.type
})

// Variante computada
const computedVariant = computed(() => {
  return props.variant
})

// Iconos por defecto según el tipo
const defaultIcons = {
  success: 'mdi-check-circle',
  error: 'mdi-alert-circle',
  warning: 'mdi-alert',
  info: 'mdi-information'
}

// Icono computado
const computedIcon = computed(() => {
  return props.icon || defaultIcons[props.type]
})

// Color del icono según el tipo si no se especifica
const iconColor = computed(() => {
  if (props.iconColor) return props.iconColor
  return props.type as any
})

// Clases del alert
const alertClasses = computed(() => ({
  [`ds-alert--${props.type}`]: true,
  'ds-alert--prominent': props.prominent,
  'ds-alert--closable': props.closable
}))

// Handler para cerrar
const handleClose = () => {
  emit('close')
}
</script>

<style scoped lang="scss">
.ds-alert {
  &__title {
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 4px;
  }

  &__content {
    font-size: 0.875rem;
    line-height: 1.5;
  }

  &__actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
  }

  &--prominent {
    .ds-alert__title {
      font-size: 1.125rem;
    }

    .ds-alert__content {
      font-size: 1rem;
    }
  }
}
</style>
